import HelperMixin from '../../../Shared/HelperMixin.vue';

// PR-12 (closing tail) / Agent-11 — 2026-05-20
// SRS §2.2 — peak/night time-window editor.
//
// Backend contract (already implemented in PricingController.cs):
//   GET  api/admin/Pricing/TimeWindows/{categoryId}
//        -> [{ id, categoryId, kind, startLocal:"HH:mm", endLocal:"HH:mm",
//             daysOfWeekMask }]
//   PUT  api/admin/Pricing/TimeWindows/{categoryId}
//        body: { windows: [...same shape, id ignored on insert...] }
//        -> { categoryId, count } and triggers a soft-delete + reinsert
//           atomic swap on the server.
//
// The original PR-12 brief asked for "POST /api/admin/Pricing/TimeWindows"
// (global). The shipped backend uses PUT-per-category, which is the safer
// contract because it scopes the atomic swap to one category and matches
// the rest of the pricing surface area. This editor binds to that PUT
// contract; if a future global POST is added, only DataService.js needs
// to change.
//
// Validation rules (mirror PricingController.ReplaceTimeWindows):
//   - kind in {1=Peak, 2=Night}
//   - HH:mm parseable
//   - start != end (midnight-crossing allowed, i.e. end < start is fine)
//   - daysOfWeekMask & 0x7F != 0
// Client-side extra rule (not enforced by backend, requested in brief):
//   - no overlapping ranges per (category, kind) on the same DOW bit.

export default {
    name: 'TimeWindowsEditor',
    mixins: [HelperMixin],

    props: {
        // Selected category id from the parent (Pricing.vue tab strip).
        categoryId: { type: Number, default: null },
        // Display-only category name, shown in the section header.
        categoryName: { type: String, default: '' }
    },

    data() {
        return {
            Windows: [],     // current editable list (rows)
            Loading: false,
            Saving: false,
            DowLabels: ['أحد', 'اثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'],
            // Per-row validation flags: { index: { startEnd, multiplier, overlap, dow, kind } }
            RowErrors: {},
            // Backend endpoint shape is "PUT per category" - this stamp tells
            // QA the editor was not silently downgraded to mock data.
            UsedMock: false,
            // Surfaces the original brief's note: backend exposes PUT not
            // POST, and the path is /TimeWindows/{categoryId} not just
            // /TimeWindows. Shown in a small footer caption.
            EndpointNote: 'PUT api/admin/Pricing/TimeWindows/{categoryId}'
        };
    },

    computed: {
        // Default windows per SRS §2.2 — used by "Reset to defaults" and as
        // a seed when the backend returns 0 rows for the category.
        DefaultWindows() {
            return [
                { id: 0, categoryId: this.categoryId, kind: 1, startLocal: '07:00', endLocal: '09:00', daysOfWeekMask: 0x7F, multiplier: 1.20 },
                { id: 0, categoryId: this.categoryId, kind: 1, startLocal: '13:00', endLocal: '15:00', daysOfWeekMask: 0x7F, multiplier: 1.20 },
                { id: 0, categoryId: this.categoryId, kind: 1, startLocal: '17:00', endLocal: '19:00', daysOfWeekMask: 0x7F, multiplier: 1.20 },
                { id: 0, categoryId: this.categoryId, kind: 2, startLocal: '22:00', endLocal: '05:00', daysOfWeekMask: 0x7F, multiplier: 1.30 }
            ];
        },
        HasErrors() {
            return Object.keys(this.RowErrors).some(k => {
                const e = this.RowErrors[k];
                return e && (e.startEnd || e.multiplier || e.overlap || e.dow || e.kind || e.format);
            });
        }
    },

    watch: {
        // Reload whenever the parent switches tabs.
        categoryId: {
            immediate: false,
            handler(v) {
                if (v) this.Load();
            }
        }
    },

    async created() {
        if (this.categoryId) await this.Load();
    },

    methods: {
        async Load() {
            if (!this.categoryId) return;
            this.Loading = true;
            this.UsedMock = false;
            this.RowErrors = {};
            try {
                const res = await this.$http.GetPricingTimeWindows(this.categoryId);
                const rows = (res.data || []).map(w => ({
                    id: w.id || 0,
                    categoryId: w.categoryId || this.categoryId,
                    kind: Number(w.kind) || 1,
                    startLocal: w.startLocal || '00:00',
                    endLocal: w.endLocal || '00:00',
                    daysOfWeekMask: Number(w.daysOfWeekMask) || 0x7F,
                    // multiplier isn't stored per-window in the current
                    // backend (PeakHourSurge/NightSurge live on PricingConfig).
                    // We keep it editable for forward-compat; saving it is a
                    // no-op until the backend adds a Multiplier column.
                    // TODO(backend): add Multiplier column to PricingTimeWindows.
                    multiplier: Number(w.multiplier) || (Number(w.kind) === 2 ? 1.30 : 1.20)
                }));
                this.Windows = rows.length ? rows : this.DefaultWindows.slice();
                if (!rows.length) this.UsedMock = true;
            } catch (e) {
                const code = e?.response?.status;
                if (code === 404 || code === 405) {
                    // Endpoint missing — seed with defaults so the operator
                    // can still draft windows and re-try save later.
                    this.UsedMock = true;
                    this.Windows = this.DefaultWindows.slice();
                } else {
                    this.Windows = [];
                    if (this.$helper && this.$helper.FailMessage)
                        this.$helper.FailMessage('تعذّر تحميل نوافذ التسعير');
                }
            } finally {
                this.Loading = false;
            }
        },

        AddRow(kind) {
            this.Windows.push({
                id: 0,
                categoryId: this.categoryId,
                kind: kind || 1,
                startLocal: kind === 2 ? '22:00' : '07:00',
                endLocal: kind === 2 ? '05:00' : '09:00',
                daysOfWeekMask: 0x7F,
                multiplier: kind === 2 ? 1.30 : 1.20
            });
        },

        RemoveRow(index) {
            this.Windows.splice(index, 1);
            // Recompute errors after removal (overlap status may change).
            this.ValidateAll();
        },

        ResetDefaults() {
            this.Windows = this.DefaultWindows.slice();
            this.RowErrors = {};
        },

        // 0x7F = all days; bit-toggle helper bound to the DOW chips in the template.
        ToggleDow(row, bit) {
            const mask = (row.daysOfWeekMask || 0) ^ (1 << bit);
            row.daysOfWeekMask = mask & 0x7F;
            this.ValidateAll();
        },
        IsDowOn(row, bit) {
            return ((row.daysOfWeekMask || 0) & (1 << bit)) !== 0;
        },

        // ---------------- VALIDATION ----------------

        ParseHHmm(s) {
            if (!s || typeof s !== 'string') return null;
            const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
            if (!m) return null;
            const h = parseInt(m[1], 10), mm = parseInt(m[2], 10);
            if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
            return h * 60 + mm;
        },

        // Returns true if two minute-of-day intervals overlap; midnight wrap
        // is handled by splitting end<start into two ranges before compare.
        RangesOverlap(aStart, aEnd, bStart, bEnd) {
            const split = (s, e) => (e <= s ? [[s, 24 * 60], [0, e]] : [[s, e]]);
            const A = split(aStart, aEnd);
            const B = split(bStart, bEnd);
            for (const [as, ae] of A) {
                for (const [bs, be] of B) {
                    if (as < be && bs < ae) return true;
                }
            }
            return false;
        },

        ValidateRow(index) {
            const row = this.Windows[index];
            const err = { startEnd: false, multiplier: false, overlap: false, dow: false, kind: false, format: false };

            // kind must be 1 (Peak) or 2 (Night).
            if (row.kind !== 1 && row.kind !== 2) err.kind = true;

            const s = this.ParseHHmm(row.startLocal);
            const e = this.ParseHHmm(row.endLocal);
            if (s === null || e === null) {
                err.format = true;
            } else if (s === e) {
                // Empty window — backend rejects this too (EMPTY_WINDOW).
                err.startEnd = true;
            }

            if (!(row.multiplier > 0) || row.multiplier > 10) err.multiplier = true;

            if (((row.daysOfWeekMask || 0) & 0x7F) === 0) err.dow = true;

            // Overlap detection vs every other row of the same kind, scoped
            // to the same category, only when at least one DOW bit overlaps.
            if (s !== null && e !== null && s !== e) {
                for (let j = 0; j < this.Windows.length; j++) {
                    if (j === index) continue;
                    const other = this.Windows[j];
                    if (other.kind !== row.kind) continue;
                    if (((other.daysOfWeekMask || 0) & (row.daysOfWeekMask || 0)) === 0) continue;
                    const os = this.ParseHHmm(other.startLocal);
                    const oe = this.ParseHHmm(other.endLocal);
                    if (os === null || oe === null || os === oe) continue;
                    if (this.RangesOverlap(s, e, os, oe)) { err.overlap = true; break; }
                }
            }

            // Vue 2 reactivity for plain-object additions.
            this.$set(this.RowErrors, String(index), err);
            return err;
        },

        ValidateAll() {
            this.RowErrors = {};
            for (let i = 0; i < this.Windows.length; i++) this.ValidateRow(i);
            return !this.HasErrors;
        },

        // Localized error message for the top-of-section banner.
        ErrorSummary() {
            const msgs = [];
            for (const k of Object.keys(this.RowErrors)) {
                const e = this.RowErrors[k];
                if (!e) continue;
                const i = Number(k) + 1;
                if (e.format)     msgs.push('السطر ' + i + ': صيغة الوقت غير صحيحة (HH:mm)');
                if (e.startEnd)   msgs.push('السطر ' + i + ': وقت البداية يساوي وقت النهاية');
                if (e.multiplier) msgs.push('السطر ' + i + ': قيمة المضاعِف يجب أن تكون بين 0 و 10');
                if (e.kind)       msgs.push('السطر ' + i + ': النوع غير صالح (الذروة/الليل)');
                if (e.dow)        msgs.push('السطر ' + i + ': يجب اختيار يوم واحد على الأقل');
                if (e.overlap)    msgs.push('السطر ' + i + ': نطاق متداخل مع سطر آخر من نفس النوع');
            }
            return msgs;
        },

        // ---------------- SAVE ----------------

        async Save() {
            if (!this.categoryId) return;
            if (!this.ValidateAll()) {
                if (this.$helper && this.$helper.FailMessage)
                    this.$helper.FailMessage('يوجد أخطاء في النوافذ — راجع الحقول الحمراء');
                return;
            }
            this.Saving = true;
            try {
                const body = {
                    windows: this.Windows.map(w => ({
                        id: w.id || 0,
                        categoryId: this.categoryId,
                        kind: w.kind,
                        startLocal: w.startLocal,
                        endLocal: w.endLocal,
                        daysOfWeekMask: w.daysOfWeekMask & 0x7F
                        // multiplier intentionally omitted — backend doesn't
                        // persist it yet (see TODO above). Surge values live
                        // on PricingConfig.PeakHourSurge / .NightSurge.
                    }))
                };
                const res = await this.$http.UpdatePricingTimeWindows(this.categoryId, body);
                const count = (res && res.data && res.data.count) || body.windows.length;
                if (this.$helper && this.$helper.SuccessMessage) {
                    this.$helper.SuccessMessage('تم حفظ ' + count + ' نافذة بنجاح');
                }
                // Refetch to confirm round-trip — required by brief step 5.
                await this.Load();
                this.$emit('saved', { categoryId: this.categoryId, count });
            } catch (e) {
                const code = e?.response?.data?.Code || e?.response?.status || 'UNKNOWN';
                const msg = ({
                    'INVALID_KIND':        'نوع غير صحيح',
                    'INVALID_TIME_FORMAT': 'صيغة الوقت غير صحيحة',
                    'EMPTY_WINDOW':        'نافذة فارغة (البداية = النهاية)',
                    'EMPTY_DOW_MASK':      'لم يتم اختيار أي يوم',
                    'INVALID_BODY':        'بيانات غير صالحة',
                }[code]) || ('فشل الحفظ — ' + code);
                if (this.$helper && this.$helper.FailMessage)
                    this.$helper.FailMessage(msg);
            } finally {
                this.Saving = false;
            }
        },

        // ---------------- TEMPLATE HELPERS ----------------

        KindLabel(kind) { return kind === 2 ? 'ليلي' : 'ذروة'; },
        RowClass(index) {
            const e = this.RowErrors[String(index)];
            if (!e) return '';
            return (e.startEnd || e.multiplier || e.overlap || e.dow || e.kind || e.format) ? 'tw-row-error' : '';
        },
        FieldErr(index, field) {
            const e = this.RowErrors[String(index)];
            if (!e) return false;
            if (field === 'start' || field === 'end') return !!(e.startEnd || e.format);
            if (field === 'multiplier') return !!e.multiplier;
            if (field === 'dow') return !!e.dow;
            if (field === 'kind') return !!e.kind;
            return false;
        }
    }
};
